import React, { useCallback, useMemo } from 'react'
import { components } from 'react-select'
import styled from 'styled-components'
import cn from 'clsx'

import HighlightBorder from 'components/Common/HighlightBorder'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import SolidTimesCircleIcon from 'components/Luxkit/Icons/solid/SolidTimesCircleIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import IconButton from 'components/Luxkit/Button/IconButton'

const Container = styled.div`
  display: flex;
  align-items: center;
`

const ControlWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;

  .select-dropdown__control {
    flex: 1;
    border-color: ${({ theme }) => theme.palette.neutral.five};
    box-shadow: none;

    &.select-dropdown__control--is-focused {
      border-color: ${({ theme }) => theme.palette.neutral.two};
    }

    .select-dropdown__value-container {
      padding: ${rem(12)};
    }
  }
`

const StyledHighlightBorder = styled(HighlightBorder)`
  box-shadow: none;

  &.has-error {
    color: ${props => props.theme.palette.messaging.critical.main};
  }
`

const ActionContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  z-index: 2;

  ${mediaQueryUp.sm} {
    display: none;
  }
`

const SearchIcon = styled(LineSearchIcon)`
  margin-right: ${rem(12)};
`

type Props = React.ComponentProps<typeof components.Control>

function LocationSearchFieldControl({ children, ...props }: Props) {
  const {
    isFocused,
    menuIsOpen,
    hasValue,
    selectProps: { hasError, inputValue, isClearable, inputRef },
    clearValue,
  } = props

  const isClearButtonVisible = useMemo(() => isClearable && Boolean(inputValue), [isClearable, inputValue])
  const isCancelButtonVisible = useMemo(() => Boolean(inputValue) || hasValue, [inputValue, hasValue])

  const clearAndKeepFocus = useCallback(() => {
    clearValue()
    inputRef.current?.focus()
  }, [clearValue, inputRef])

  return (
    <Container>
      <ControlWrapper>
        <components.Control {...props}>
          <StyledHighlightBorder
            visible={isFocused || menuIsOpen || hasError}
            className={cn({ 'has-error': hasError, 'highlight-border': true })}
          />
          {children}
        </components.Control>
        <ActionContainer>
          { isClearButtonVisible &&
            <IconButton kind="tertiary" size="large" onClick={clearAndKeepFocus}>
              <SolidTimesCircleIcon />
            </IconButton>
          }
          { !isClearButtonVisible &&
            <SearchIcon size={20} />
          }
        </ActionContainer>
      </ControlWrapper>
      {isCancelButtonVisible && (
        <TextButton kind="tertiary" className="cancel-search-button" onClick={clearValue}>
          Cancel
        </TextButton>
      )}
    </Container>
  )
}

export default LocationSearchFieldControl
